import React, { useEffect } from 'react';
import styled from 'styled-components';

import { useEventing } from 'commons/eventing';
import { Shift } from 'craft/components/Shortcuts/Shift';
import { H3, H4, Subhead } from 'styleguide/typography';

import { BackspaceShortcut } from './BackspaceShortcut';
import { Cmd } from './Cmd';
import { Shortcut } from './Shortcut';

const Container = styled.div`
  padding: 1em;
  bottom: 0;
  user-select: none;
  overflow: hidden;
`;

const ShortcutHeading = styled(H4)`
  height: 20px;
  margin: 1.6em 0;
`;

const ShortcutTitle = styled(H3)`
  display: inline-block;
  margin-right: 1em;
  margin-bottom: 1em;
`;

const ShortcutEntryContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ShortcutsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 5em;
`;

const ShortcutSection = ({
  name,
  children,
}: {
  name?: string;
  children: JSX.Element[] | JSX.Element;
}) => (
  <div>
    <ShortcutHeading>{name}</ShortcutHeading>
    <ShortcutsGrid>{children}</ShortcutsGrid>
  </div>
);

const ShortcutEntry = ({
  name,
  children,
}: {
  name: string;
  children: JSX.Element[] | JSX.Element;
}) => (
  <ShortcutEntryContainer>
    <Subhead css={{ width: '8em' }}>{name}</Subhead>
    <Subhead>{children}</Subhead>
  </ShortcutEntryContainer>
);

export const ShortcutsTab = () => {
  const eventing = useEventing();

  useEffect(() => {
    eventing.screen('editor.view.shortcutsReference');
  }, []);

  return (
    <Container>
      <ShortcutTitle>Shortcuts Panel</ShortcutTitle>
      <Shortcut>
        <Shift />
      </Shortcut>
      <Shortcut>?</Shortcut>
      <ShortcutSection name="General">
        <ShortcutEntry name="Copy">
          <Cmd />
          <Shortcut>C</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Multi-select">
          <Shortcut>
            <Shift />
          </Shortcut>
          <Shortcut>Click</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Paste">
          <Cmd />
          <Shortcut>V</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Undo">
          <Cmd />
          <Shortcut>Z</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Paste Into">
          <Shortcut>
            <Shift />
          </Shortcut>
          <Cmd />
          <Shortcut>V</Shortcut>
        </ShortcutEntry>
        {/* <ShortcutEntry name="Duplicate"><Cmd /><Shortcut>D</Shortcut></ShortcutEntry> */}
        <ShortcutEntry name="Redo">
          <Cmd />
          <Shortcut>Y</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Delete">
          <BackspaceShortcut />
        </ShortcutEntry>
        {/* <ShortcutEntry name="Select all"><Cmd /><Shortcut>A</Shortcut></ShortcutEntry> */}
      </ShortcutSection>
      <ShortcutSection name="Toolbox">
        <ShortcutEntry name="Components">
          <Shortcut>C</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Styles">
          <Shortcut>S</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Interactions">
          <Shortcut>I</Shortcut>
        </ShortcutEntry>
      </ShortcutSection>
      <ShortcutSection name="Text">
        <ShortcutEntry name="Select all">
          <Cmd />
          <Shortcut>A</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Bold">
          <Cmd />
          <Shortcut>B</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Italic">
          <Cmd />
          <Shortcut>I</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Underline">
          <Cmd />
          <Shortcut>U</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Embed Link">
          <Cmd />
          <Shortcut>K</Shortcut>
        </ShortcutEntry>
        {/* <ShortcutEntry name="Inline Code"><Cmd /><Shortcut>E</Shortcut></ShortcutEntry> */}
      </ShortcutSection>
      <ShortcutSection name="Menus">
        <ShortcutEntry name="Layout">
          <Shortcut>L</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Text">
          <Shortcut>T</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Containers">
          <Shortcut>N</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Elements">
          <Shortcut>E</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Media">
          <Shortcut>M</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Object">
          <Shortcut>O</Shortcut>
        </ShortcutEntry>
        <ShortcutEntry name="Embed">
          <Shortcut>D</Shortcut>
        </ShortcutEntry>
      </ShortcutSection>
    </Container>
  );
};
